import React, { FC, Fragment, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, Typography, makeStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useHistory } from 'react-router';

import { ContentEmailSpec } from 'api';
import { Loaders, ValidatedTextField } from 'components';
import { useNotify } from 'hooks';

import Recipients from './Recipients';
import Title from './Title';
import When from './When';
import Message from './Message';
import Image from './Image';
import Content from './Content';
import PreviewDialog from './Preview';

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(4),
  },
  spacer: {
    marginTop: theme.spacing(3),
  },
  submitWrapper: {
    minWidth: 150,
  },
}));

const Email: FC = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const notify = useNotify();

  const { isSubmitting, submitForm, validateForm, values } = useFormikContext<ContentEmailSpec>();

  async function handlePreview() {
    const result = await validateForm();
    const hasErrors = !!Object.keys(result).length;

    if (hasErrors) {
      await submitForm();
      return notify.error({ i18n: { i18nKey: 'contentEmails:previewError' } });
    }

    setOpen(true);
  }

  function renderEmailMeta() {
    return (
      <Fragment>
        <Grid container alignItems="center">
          <Grid item xs={3}>
            <Typography variant="subtitle2">{t('contentEmails:fromAddress')}</Typography>
          </Grid>
          <Grid item xs={9}>
            <ValidatedTextField
              name="email_from"
              i18nKey="contentEmails:fromAddress"
              color="primary"
              TextFieldProps={{ margin: 'none', label: null }}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.spacer} alignItems="center">
          <Grid item xs={3}>
            <Typography variant="subtitle2">{t('contentEmails:replyTo')}</Typography>
          </Grid>
          <Grid item xs={9}>
            <ValidatedTextField
              name="email_reply_to"
              i18nKey="contentEmails:replyToPlaceholder"
              color="primary"
              TextFieldProps={{ margin: 'none', label: null }}
            />
          </Grid>
        </Grid>
      </Fragment>
    );
  }

  function renderOptions() {
    return (
      <Grid container>
        <Grid item xs={3}>
          <Typography variant="subtitle2">{t('common:options')}</Typography>
        </Grid>
        <Grid item xs={9}>
          <Image />
        </Grid>
      </Grid>
    );
  }

  function renderSubmit() {
    if (isSubmitting) {
      return (
        <div className={classes.submitWrapper}>
          <Loaders.Standard height={38} />
        </div>
      );
    }

    return (
      <div className={classes.submitWrapper}>
        <Button type="submit" size="large" variant="contained" color="primary" fullWidth>
          {t('contentEmails:saveEmail')}
        </Button>
      </div>
    );
  }

  return (
    <Fragment>
      <Title />
      <Divider className={classes.divider} />
      <Recipients />
      <Divider className={classes.divider} />
      <When />
      <Divider className={classes.divider} />
      {renderEmailMeta()}
      <Divider className={classes.divider} />
      <Message />
      <Divider className={classes.divider} />
      {renderOptions()}
      <Divider className={classes.divider} />
      <Content />
      <Divider className={classes.divider} />
      <Grid container justify="space-between">
        <Grid item>
          <Button startIcon={<DeleteIcon />} onClick={() => history.goBack()}>
            {t('common:cancel')}
          </Button>
        </Grid>
        <Grid item>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Button size="large" variant="outlined" onClick={handlePreview}>
                {t('common:preview')}
              </Button>
            </Grid>
            <Grid item>{renderSubmit()}</Grid>
          </Grid>
        </Grid>
      </Grid>
      <PreviewDialog open={open} onClose={() => setOpen(false)} values={values} />
    </Fragment>
  );
};

export default Email;
